import type { Building } from "building-types/building/buildingTypes";
import { RelativeVerticalDatum } from "building-types/datums/datumsTypesEnum";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { isStairSpecificVerticalDatum } from "../utils/datumTools";

export function getRelativeDatumHeight(
  datum: string,
  levelId: string,
  levels: Building["levels"],
  queryZ?: (x: number, y: number) => WorldInches,
  x?: number,
  y?: number,
): WorldInches {
  let datumHeight: number = 0;

  if (isStairSpecificVerticalDatum[datum]) {
    if (queryZ === undefined || x === undefined || y === undefined) {
      throw new Error(
        `getRelativeDatumHeight: topPlaneCandidates, x, and y must be defined for stair specific vertical datum ${datum}`,
      );
    }
  }

  switch (datum) {
    /** this is the bottom of the host level */
    case RelativeVerticalDatum.TOP_OF_FF_HOST: {
      const { floorElevation } = levels[levelId];
      datumHeight = floorElevation;
      break;
    }
    /** this is the top plate of the host level. there may be a level above */
    case RelativeVerticalDatum.TOP_OF_WALL_PLATE_HOST: {
      const { floorElevation, topOfWall } = levels[levelId];
      datumHeight = floorElevation + topOfWall;
      break;
    }
    /** this is the top of the level. there may be a level above or not*/
    /** if there is no level above then it levelAboveHost defaults to level and returns top of wall plate height */
    case RelativeVerticalDatum.TOP_OF_FF_ABOVE_HOST: {
      const { levelAbove } = levels[levelId];
      const levelAboveHost = levelAbove ? levels[levelAbove] : undefined;
      if (levelAboveHost) {
        const { floorElevation, topOfWall } = levels[levelId];
        const floorPlateThicknessAbove: number = levelAboveHost.levelAbove
          ? levels[levelAboveHost.levelAbove].floorPlateThickness
          : 0;
        datumHeight = floorElevation + topOfWall + floorPlateThicknessAbove;
      }
      break;
    }
    /** this is the bottom of the level. there may be a level below or not*/
    /** if there is no level below then it levelBelowHost defaults to level and returns floor elevation */
    case RelativeVerticalDatum.TOP_OF_FF_FINISH_BELOW_HOST: {
      const { levelBelow } = levels[levelId];
      const levelBelowHost = levelBelow ? levels[levelBelow] : undefined;
      if (levelBelowHost) {
        const { floorElevation } = levelBelowHost;
        datumHeight = floorElevation;
      }
      break;
    }
    case RelativeVerticalDatum.TOP_OF_WALL_PLATE_ABOVE_HOST: {
      const { levelAbove } = levels[levelId];
      const levelAboveHost = levelAbove ? levels[levelAbove] : undefined;
      if (levelAboveHost) {
        const { floorElevation, topOfWall } = levelAboveHost;
        datumHeight = floorElevation + topOfWall;
      }
      break;
    }
    case RelativeVerticalDatum.TOP_OF_WALL_PLATE_BELOW_HOST: {
      const { levelBelow } = levels[levelId];
      const levelBelowHost = levelBelow ? levels[levelBelow] : undefined;
      if (levelBelowHost) {
        const { floorElevation, topOfWall } = levelBelowHost;
        datumHeight = floorElevation + topOfWall;
      }
      break;
    }
    case RelativeVerticalDatum.STAIR_SYSTEM: {
      assertIsNotUndefined(queryZ);
      assertIsNotUndefined(x);
      assertIsNotUndefined(y);

      datumHeight = queryZ(x, y);
      break;
    }
    case RelativeVerticalDatum.BOTTOM_OF_STAIR_SYSTEM_HOST: {
      const { levelBelow } = levels[levelId];
      const levelBelowHost = levelBelow ? levels[levelBelow] : undefined;
      if (levelBelowHost) {
        const { floorElevation, topOfWall } = levelBelowHost;
        datumHeight = floorElevation + topOfWall;
      } else {
        const {
          levelDatumsGenerated: { grade },
        } = levels[levelId];
        datumHeight = grade;
      }
      break;
    }
    default:
      break;
  }
  return worldInchesNew(datumHeight);
}
